import {
  AlertTriangle,
  Banknote,
  FileText,
  FolderKanban,
  ListChecks,
  PackageMinus,
  ShoppingCart,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import { EmptyState } from '@proerp/ui';
import type { Permission } from '@proerp/shared';
import { describeError } from '@/lib/apiClient';
import { formatMoney } from '@/lib/money';
import { usePermissions } from '@/lib/useCurrentUser';
import { useOpenItems } from '@/features/accounting/accountingApi';
import { useMaintenanceSchedules } from '@/features/assembly/assemblyApi';
import { useProjectTaskSummary } from '@/features/projects/projectTasksApi';
import { useDashboardKpis } from '../dashboardApi';
import { yearOverYearPercent } from '../dashboardMetrics';
import { SectionCards, type KpiCardData } from '../SectionCards';

/** Fällig in ≤ 30 Tagen oder bereits überfällig. */
function isDueSoon(nextDueAt: string): boolean {
  return new Date(nextDueAt).getTime() <= Date.now() + 30 * 24 * 60 * 60 * 1000;
}

type GatedKpi = KpiCardData & {
  /** Alle nötigen Rechte (UND). Leer = für jeden sichtbar. */
  permissions: Permission[];
  /** Der Server hat das Feld mangels Recht leer gelassen (`null`) → Kachel ausblenden. */
  isWithheld?: boolean;
};

/** Fehlertext einer Abfrage — oder null, solange sie nicht gescheitert ist. */
function errorOf(query: { isError: boolean; error: unknown }): string | null {
  return query.isError ? describeError(query.error) : null;
}

/**
 * KPI-Kachelraster. Jede Kachel deklariert ihre Berechtigungen; finanzielle Kacheln
 * (Umsatz, Offene Posten, Überfällige Rechnungen) verlangen `dashboard.viewfinancials`
 * (ADR 0062), operative Kacheln die jeweilige Modul-Leseberechtigung. Es werden nur
 * die Kacheln gerendert, die der Nutzer sehen darf — nie eine genullte „0,00 €"-Kachel.
 *
 * Umsatz, offene Angebote, aktive Projekte, offene Bestellungen und Mindestbestand
 * rechnet der Server über ALLE Datensätze (`GET /v1/dashboard/kpis`); früher zählte
 * diese Komponente die erste Listenseite (≤ 200 Zeilen) und lag ab dem 201.
 * Datensatz falsch. Die Kachel-Rechte spiegeln die Feld-Rechte des Servers; lässt
 * er ein Feld trotzdem leer (`null`), verschwindet die Kachel.
 */
export function KpiWidget() {
  const { can } = usePermissions();
  const dashboardKpis = useDashboardKpis();
  const taskSummary = useProjectTaskSummary();
  const openItems = useOpenItems();
  const maintenance = useMaintenanceSchedules();

  const serverData = dashboardKpis.data?.data;
  const serverError = errorOf(dashboardKpis);
  /** Geladen, aber vom Server mangels Recht nicht befüllt. */
  const isWithheld = (value: unknown): boolean => serverData !== undefined && value === null;

  const revenue = serverData?.revenue ?? null;
  const currentYear = revenue?.year ?? new Date().getFullYear();
  const revenueMicros = revenue ? BigInt(revenue.currentYearNet.amount) : 0n;
  const revenueDeltaPct = revenue ? yearOverYearPercent(revenueMicros, BigInt(revenue.previousYearNet.amount)) : null;
  const revenueDelta =
    revenueDeltaPct === null
      ? null
      : { text: `${revenueDeltaPct >= 0 ? '+' : ''}${revenueDeltaPct} % ggü. ${currentYear - 1}`, positive: revenueDeltaPct >= 0 };

  const openQuotes = serverData?.openQuotes ?? null;
  const openQuoteCount = openQuotes?.count ?? 0;
  const activeProjects = serverData?.activeProjects ?? null;
  const purchaseOrders = serverData?.purchaseOrders ?? null;
  const lowStock = serverData?.articlesBelowMinStock ?? 0;

  const openList = openItems.data?.data ?? [];
  const openTotal = openList.reduce((sum, item) => sum + BigInt(item.open.amount), 0n);
  const overdue = openList.filter((item) => item.isOverdue);

  const dueMaintenance = (maintenance.data?.data ?? []).filter(
    (schedule) => schedule.isActive && isDueSoon(schedule.nextDueAt),
  ).length;

  const fromServer = { isLoading: dashboardKpis.isLoading, error: serverError };

  const tiles: GatedKpi[] = [
    {
      // Anzeige-Recht + Rohdaten-Recht, wie der Server (ADR 0062, Defense in Depth).
      permissions: ['dashboard.viewfinancials', 'sales.read'],
      isWithheld: isWithheld(serverData?.revenue),
      label: `Umsatz ${currentYear}`,
      value: formatMoney({ amount: revenueMicros.toString(), currency: 'EUR' }),
      icon: TrendingUp,
      to: '/verkauf',
      ...fromServer,
      delta: revenueDelta,
      hint: 'fakturiert, laufendes Jahr',
    },
    {
      permissions: ['sales.read'],
      isWithheld: isWithheld(serverData?.openQuotes),
      label: 'Offene Angebote',
      value: String(openQuoteCount),
      icon: FileText,
      to: '/verkauf',
      ...fromServer,
      // Das €-Volumen ist eine Finanzangabe — der Server liefert es nur mit dashboard.viewfinancials.
      hint:
        openQuoteCount > 0 && openQuotes?.grossVolume
          ? `${formatMoney(openQuotes.grossVolume)} Volumen`
          : openQuoteCount > 0
            ? 'offen'
            : 'keine offen',
    },
    {
      permissions: ['projects.read'],
      isWithheld: isWithheld(serverData?.activeProjects),
      label: 'Aktive Projekte',
      value: String(activeProjects ?? 0),
      icon: FolderKanban,
      to: '/projekte',
      ...fromServer,
      hint: 'laufend (nicht geschlossen)',
    },
    {
      permissions: ['projects.read'],
      label: 'Offene Projektaufgaben',
      value: String(taskSummary.data?.data.open ?? 0),
      icon: ListChecks,
      to: '/projekte',
      isLoading: taskSummary.isLoading,
      error: errorOf(taskSummary),
      isAlert: (taskSummary.data?.data.dueSoon ?? 0) > 0,
      hint:
        (taskSummary.data?.data.dueSoon ?? 0) > 0
          ? `${taskSummary.data?.data.dueSoon} fällig in ≤ 7 Tagen (${taskSummary.data?.data.overdue} überfällig)`
          : 'keine kritischen Deadlines',
    },
    {
      permissions: ['dashboard.viewfinancials'],
      label: 'Offene Posten',
      value: formatMoney({ amount: openTotal.toString(), currency: 'EUR' }),
      icon: Banknote,
      to: '/buchhaltung',
      isLoading: openItems.isLoading,
      error: errorOf(openItems),
      hint: `${openList.length} Rechnung${openList.length === 1 ? '' : 'en'} offen`,
    },
    {
      permissions: ['dashboard.viewfinancials'],
      label: 'Überfällige Rechnungen',
      value: String(overdue.length),
      icon: AlertTriangle,
      to: '/buchhaltung',
      isLoading: openItems.isLoading,
      error: errorOf(openItems),
      isAlert: overdue.length > 0,
      hint: overdue.length > 0 ? 'Handlung nötig' : 'alles im Plan',
    },
    {
      permissions: ['procurement.read'],
      isWithheld: isWithheld(serverData?.purchaseOrders),
      label: 'Offene Bestellungen',
      value: String(purchaseOrders?.open ?? 0),
      icon: ShoppingCart,
      to: '/beschaffung',
      ...fromServer,
      hint: `${purchaseOrders?.total ?? 0} gesamt`,
    },
    {
      permissions: ['assembly.read'],
      label: 'Fällige Wartungen',
      value: String(dueMaintenance),
      icon: Wrench,
      to: '/montage',
      isLoading: maintenance.isLoading,
      error: errorOf(maintenance),
      isAlert: dueMaintenance > 0,
      hint: dueMaintenance > 0 ? '≤ 30 Tage' : 'nichts fällig',
    },
    {
      permissions: ['inventory.read'],
      isWithheld: isWithheld(serverData?.articlesBelowMinStock),
      label: 'Artikel unter Mindestbestand',
      value: String(lowStock),
      icon: PackageMinus,
      to: '/einkauf',
      ...fromServer,
      isAlert: lowStock > 0,
      hint: lowStock > 0 ? 'Nachschub prüfen' : 'Bestände ok',
    },
  ];

  const visible = tiles.filter((kpi) => kpi.permissions.every((permission) => can(permission)) && !kpi.isWithheld);
  if (visible.length === 0) {
    return <EmptyState icon={FolderKanban} title="Keine Kennzahlen für deine Rolle freigegeben." />;
  }
  return <SectionCards kpis={visible} />;
}
